import { useState } from 'react';
import {
  Box, Typography, Paper, TextField, Button, Table, TableHead, TableBody,
  TableRow, TableCell, Chip, FormControl, InputLabel, Select, MenuItem, Alert
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { Search } from '@mui/icons-material';
import { kardexService } from '../services/kardexService';
import type { KardexResponse, KardexGeneralItem } from '../types/kardex.types';
import { almacenService } from '../../almacenes/services/almacenService';
import type { Deposito } from '../../almacenes/types/almacen.types';
import { useEffect } from 'react';

const KardexPage = () => {
  const [idArticulo, setIdArticulo] = useState<string>('');
  const [idDeposito, setIdDeposito] = useState<number | ''>('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [kardex, setKardex] = useState<KardexResponse | null>(null);
  const [generalItems, setGeneralItems] = useState<KardexGeneralItem[]>([]);
  const [depositos, setDepositos] = useState<Deposito[]>([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'detail' | 'general'>('general');

  useEffect(() => {
    loadDepositos();
    loadGeneral();
  }, []);

  const loadDepositos = async () => {
    try { setDepositos(await almacenService.getAll()); } catch { /* handled */ }
  };

  const loadGeneral = async () => {
    setLoading(true);
    try { setGeneralItems(await kardexService.getKardexGeneral()); } finally { setLoading(false); }
  };

  const handleSearch = async () => {
    if (!idArticulo) return;
    setLoading(true);
    try {
      const result = await kardexService.getKardex(
        Number(idArticulo),
        idDeposito ? Number(idDeposito) : undefined,
        fechaDesde || undefined,
        fechaHasta || undefined
      );
      setKardex(result);
      setViewMode('detail');
    } finally { setLoading(false); }
  };

  const generalColumns: GridColDef[] = [
    { field: 'codigoArticulo', headerName: 'Código', width: 120 },
    { field: 'descripcionArticulo', headerName: 'Artículo', flex: 1, minWidth: 200 },
    { field: 'totalEntradas', headerName: 'Entradas', width: 120, type: 'number' },
    { field: 'totalSalidas', headerName: 'Salidas', width: 120, type: 'number' },
    { field: 'saldoActual', headerName: 'Saldo', width: 120, type: 'number',
      renderCell: (params) => <Chip label={params.value} size="small" color={params.value < 0 ? 'error' : 'success'} />
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight="bold" mb={3}>Kardex de Inventario</Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField size="small" label="ID Artículo" value={idArticulo}
            onChange={(e) => setIdArticulo(e.target.value)} sx={{ width: 120 }} />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Almacén</InputLabel>
            <Select value={idDeposito} label="Almacén" onChange={(e) => setIdDeposito(e.target.value as number | '')}>
              <MenuItem value="">Todos</MenuItem>
              {depositos.map((d) => <MenuItem key={d.idDeposito} value={d.idDeposito}>{d.nombre}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField size="small" label="Desde" type="date" value={fechaDesde}
            onChange={(e) => setFechaDesde(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField size="small" label="Hasta" type="date" value={fechaHasta}
            onChange={(e) => setFechaHasta(e.target.value)} InputLabelProps={{ shrink: true }} />
          <Button variant="contained" startIcon={<Search />} onClick={handleSearch}>Consultar</Button>
          <Button variant="outlined" onClick={() => { setViewMode('general'); loadGeneral(); }}>Ver General</Button>
        </Box>
      </Paper>

      {viewMode === 'general' && (
        <Paper sx={{ height: 500 }}>
          <DataGrid rows={generalItems} columns={generalColumns} getRowId={(row) => row.idArticulo}
            loading={loading} density="compact"
            onRowClick={(params) => { setIdArticulo(params.row.idArticulo.toString()); handleSearch(); }}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
        </Paper>
      )}

      {viewMode === 'detail' && kardex && (
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
            <Typography><strong>Artículo:</strong> {kardex.codigoArticulo} - {kardex.descripcionArticulo}</Typography>
            <Chip label={`Saldo Inicial: ${kardex.saldoInicial}`} size="small" />
            <Chip label={`Entradas: ${kardex.totalEntradas}`} size="small" color="success" />
            <Chip label={`Salidas: ${kardex.totalSalidas}`} size="small" color="error" />
            <Chip label={`Saldo Final: ${kardex.saldoFinal}`} size="small" color="primary" />
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Fecha</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell>Almacén</TableCell>
                <TableCell align="right">Entrada</TableCell>
                <TableCell align="right">Salida</TableCell>
                <TableCell align="right">Saldo</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {kardex.lineas.map((l, i) => (
                <TableRow key={i}>
                  <TableCell>{new Date(l.fecha).toLocaleDateString()}</TableCell>
                  <TableCell><Chip label={l.tipoMovimiento} size="small" color={l.tipoMovimiento === 'E' ? 'success' : l.tipoMovimiento === 'S' ? 'error' : 'info'} /></TableCell>
                  <TableCell>{l.descripcionMovimiento}</TableCell>
                  <TableCell>{l.depositoNombre}</TableCell>
                  <TableCell align="right">{l.entrada > 0 ? l.entrada : '-'}</TableCell>
                  <TableCell align="right">{l.salida > 0 ? l.salida : '-'}</TableCell>
                  <TableCell align="right"><strong>{l.saldo}</strong></TableCell>
                </TableRow>
              ))}
              {kardex.lineas.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center">No hay movimientos en el período</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      {viewMode === 'detail' && !kardex && !loading && (
        <Alert severity="info">Ingrese un ID de artículo y presione "Consultar"</Alert>
      )}
    </Box>
  );
};

export default KardexPage;
